import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Phone, PhoneCall, MessageCircle, Send, Save, User, Settings, RefreshCw, PhoneOff, Loader2, ChevronLeft, ChevronRight, MapPin, Copy, Check, X, Trash2, ImagePlus, Download, FileText, Bot } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../hooks/useAuth";
import { createLeadComment, deleteLeadComment, updateLead, getLeadDataById, fetchDistinctValues, uploadScreenshots, deleteScreenshot } from "../../services/crmServices/leadService";
import { fetchTemplates } from "../../services/crmServices/templateService";
import { serviceConstants } from "../../services/common/constants";
import { getFullName } from "../../services/common/util";
import { format } from "date-fns";
import moment from "moment-timezone";
import { useSelector } from "react-redux";
import { selectLeadStatuses } from "../../store/slices/leadStatusSlice";
import { selectAllLeads } from "../../store/slices/leadsSlice";
import { fetchUsers } from "../../services/crmServices/userService";
import {
    initiateVoisoCall,
    initiateCommpeakCall,
    initiateCoperatoCall,
    initiateSquareTalkCall,
    initiateOctellaCall,
    initiateCccCall,
    getSquareTalkCallStatus,
    handleCallCompletion,
    endCall
} from "../../services/voipServices";
import { createLog } from "../../services/crmServices/logService";

export function VoiceCallModal({ open, onClose, lead, onSuccess, onLeadUpdate, filteredLeads = [], currentLeadIndex = -1, onNavigate, totalLeads = 0, currentPage = 1, pageSize = 100, onPageChange }) {
    const { user } = useAuth();
    const leadStatuses = useSelector(selectLeadStatuses);
    const allLeadsFromRedux = useSelector(selectAllLeads); // Get all leads from Redux to access updated data
    const [localLead, setLocalLead] = useState(lead);
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        status: "new",
        source: "",
        balance: "",
        balanceCurrency: "USD"
    });
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState("");
    const [transcriptCall, setTranscriptCall] = useState(null); // AI call transcript modal
    const [selectedProvider, setSelectedProvider] = useState("");
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [activeCall, setActiveCall] = useState(null);
    const [endingCall, setEndingCall] = useState(false);
    const pollingIntervalRef = useRef(null);
    const [localTime, setLocalTime] = useState("");
    const [emailCopied, setEmailCopied] = useState(false);
    const [sourceOptions, setSourceOptions] = useState([]);
    const [screenshots, setScreenshots] = useState([]);
    const [uploadingScreenshots, setUploadingScreenshots] = useState(false);
    const [showScreenshots, setShowScreenshots] = useState(false);
    
    const [templates, setTemplates] = useState([]);
    const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
    const [addingComment, setAddingComment] = useState(false);
    const lastCommentClickRef = useRef(0);
    const [navigationLoading, setNavigationLoading] = useState(false);
    const lastNavigationClickRef = useRef(0);
    const lastCheckedLeadIdRef = useRef(null);
    const leadIdRef = useRef(lead?._id);
    const isSavingRef = useRef(false);
    
    // Sync localLead with prop changes, but only when lead ID actually changes
    // This prevents re-syncing when parent re-renders with same lead data
    useEffect(() => {
        // Only sync if the lead ID has changed (new lead) or if localLead is null
        if (!localLead || (lead?._id && lead._id !== leadIdRef.current)) {
            leadIdRef.current = lead?._id;
            setLocalLead(lead);
        }
    }, [lead?._id]); // Only depend on lead ID, not the entire lead object
    
    // Prevent Dialog from closing during save operations
    const handleDialogOpenChange = (newOpen) => {
        // Don't allow closing if we're currently saving
        if (!newOpen && isSavingRef.current) {
            // Prevent closing during save - do nothing
            return;
        }
        // Only call onClose if we're actually closing (newOpen is false)
        if (!newOpen) {
            onClose();
        }
    };
    
    // Note: We intentionally DON'T auto-navigate away when a lead is filtered out
    // The lead should remain in the modal so the agent can continue working on it
    // The lead will be updated via onLeadUpdate when saved, keeping it in sync
    
    // Navigation helpers - simple approach: only navigate within current page
    const hasNavigation = currentLeadIndex >= 0 && filteredLeads.length > 0;
    const indexInPage = currentLeadIndex % pageSize;
    
    // Always allow navigation (will refresh and restart when reaching page boundaries)
    const canGoNext = hasNavigation;
    const canGoPrevious = hasNavigation;
    
    const totalLeadsCount = totalLeads || filteredLeads.length;
    const leadPosition = hasNavigation ? `Lead ${currentLeadIndex + 1} of ${totalLeadsCount.toLocaleString()}` : null;
    
    // Get timezone from country (simple mapping for common countries)
    const getTimezoneFromCountry = (country) => {
        const countryTimezones = {
            'United States': 'America/New_York',
            'United Kingdom': 'Europe/London',
            'Germany': 'Europe/Berlin',
            'France': 'Europe/Paris',
            'Spain': 'Europe/Madrid',
            'Italy': 'Europe/Rome',
            'Canada': 'America/Toronto',
            'Australia': 'Australia/Sydney',
            'Japan': 'Asia/Tokyo',
            'China': 'Asia/Shanghai',
            'India': 'Asia/Kolkata',
            'Brazil': 'America/Sao_Paulo',
            'Mexico': 'America/Mexico_City',
            'Netherlands': 'Europe/Amsterdam',
            'Sweden': 'Europe/Stockholm',
            'Norway': 'Europe/Oslo',
            'Denmark': 'Europe/Copenhagen',
            'Poland': 'Europe/Warsaw',
            'UAE': 'Asia/Dubai',
            'Saudi Arabia': 'Asia/Riyadh',
            'South Africa': 'Africa/Johannesburg',
            'Singapore': 'Asia/Singapore',
            'Malaysia': 'Asia/Kuala_Lumpur',
            'Thailand': 'Asia/Bangkok',
            'Philippines': 'Asia/Manila',
            'Indonesia': 'Asia/Jakarta',
            'Turkey': 'Europe/Istanbul',
            'Russia': 'Europe/Moscow',
            'Israel': 'Asia/Jerusalem',
            'Egypt': 'Africa/Cairo'
        };
        return countryTimezones[country] || 'UTC';
    };
    
    // Update local time every second
    useEffect(() => {
        if (!localLead?.country) {
            setLocalTime("");
            return;
        }
        
        const updateTime = () => {
            const timezone = getTimezoneFromCountry(localLead.country);
            const time = moment().tz(timezone).format('HH:mm:ss');
            const date = moment().tz(timezone).format('MMM DD, YYYY');
            setLocalTime(`${time} (${date})`);
        };
        
        updateTime();
        const interval = setInterval(updateTime, 1000);
        return () => clearInterval(interval);
    }, [localLead?.country]);

    // Initialize form data when lead changes
    useEffect(() => {
        if (localLead) {
            setFormData({
                firstName: localLead.firstName || "",
                lastName: localLead.lastName || "",
                phone: localLead.phone || "",
                email: localLead.email || "",
                status: localLead.status || "new",
                source: localLead.source || "",
                balance: localLead.balance || "",
                balanceCurrency: localLead.balanceCurrency || "USD"
            });
            // Load screenshots
            if (localLead.screenshots && localLead.screenshots.length) {
                setScreenshots(localLead.screenshots);
            } else {
                setScreenshots([]);
            }
            
            // Sort comments by createdAt descending (newest first) and ensure proper structure
            const sortedComments = (localLead.comments || [])
                .map(comment => ({
                    ...comment,
                    id: comment._id || comment.id || Math.random().toString(36).substr(2, 9),
                    isSystem: comment.isSystem || false
                }))
                .sort((a, b) => {
                    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                    return dateB - dateA; // Descending order (newest first)
                });
            setComments(sortedComments);
        }
        fetchUsers().then((res) => {
            setUsers(res.data);
        });
    }, [localLead?._id]); // Only re-initialize when lead ID changes, not on every lead update

    // Fetch source options when modal opens
    useEffect(() => {
        const loadSources = async () => {
            try {
                const response = await fetchDistinctValues('source');
                if (response?.data) {
                    setSourceOptions(response.data.map(source => ({ 
                        label: source, 
                        value: source 
                    })));
                }
            } catch (error) {
                console.error("Error fetching sources:", error);
            }
        };
        
        if (open) {
            loadSources();
        }
    }, [open]);

    // Fetch comment templates when modal opens
    useEffect(() => {
        if (open) {
            fetchTemplates()
                .then((res) => setTemplates(res?.data || []))
                .catch(() => setTemplates([]));
        }
    }, [open]);

    // Store activeCall in ref to avoid closure issues
    const activeCallRef = useRef(activeCall);
    useEffect(() => {
        activeCallRef.current = activeCall;
    }, [activeCall]);

    // Automatic call status polling and completion detection
    useEffect(() => {
        // Clear any existing polling interval
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }

        // Only poll for SquareTalk (has call status API with full tracking)
        // All other providers use simple notification only
        if (!activeCall || !activeCall.id || !lead?._id || activeCall.provider !== "squaretalk") {
            return;
        }

        const maxPollAttempts = 120; // Poll for up to 10 minutes (120 * 5 seconds)
        let pollAttempts = 0;

        const checkCallStatus = async () => {
            // Check current activeCall from ref (to avoid closure issues)
            const currentActiveCall = activeCallRef.current;
            
            // Stop if activeCall was cleared or max attempts reached
            if (!currentActiveCall || !currentActiveCall.id || pollAttempts >= maxPollAttempts) {
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
                return;
            }

            pollAttempts++;

            try {
                let statusResponse;
                
                // Get call status based on provider
                switch (currentActiveCall.provider) {
                    case "squaretalk":
                        statusResponse = await getSquareTalkCallStatus(currentActiveCall.id);
                        break;
                    // Add other providers as needed
                    default:
                        return;
                }

                if (statusResponse?.success && statusResponse?.data) {
                    const callData = statusResponse.data;
                    // SquareTalk API returns status in different possible fields
                    const callStatus = callData.status || callData.state || callData.call_status || callData.state;

                    // Check if call is completed
                    const completedStatuses = ["completed", "finished", "ended", "terminated", "closed", "success"];
                    const failedStatuses = ["failed", "busy", "no-answer", "rejected", "cancelled", "error"];

                    if (completedStatuses.includes(callStatus?.toLowerCase()) || 
                        failedStatuses.includes(callStatus?.toLowerCase())) {
                        
                        // Double-check activeCall still exists before processing completion
                        const finalActiveCall = activeCallRef.current;
                        if (!finalActiveCall) {
                            // Call was cleared, stop polling
                            if (pollingIntervalRef.current) {
                                clearInterval(pollingIntervalRef.current);
                                pollingIntervalRef.current = null;
                            }
                            return;
                        }

                        // Calculate duration
                        const endTime = new Date();
                        const startTime = finalActiveCall.startTime ? new Date(finalActiveCall.startTime) : new Date();
                        const duration = Math.floor((endTime - startTime) / 1000); // Duration in seconds

                        // Stop polling
                        if (pollingIntervalRef.current) {
                            clearInterval(pollingIntervalRef.current);
                            pollingIntervalRef.current = null;
                        }

                        // Handle call completion
                        try {
                            await handleCallCompletion({
                                provider: finalActiveCall.provider,
                                callId: finalActiveCall.id,
                                leadId: lead._id,
                                agentId: user._id,
                                duration: duration,
                                status: callStatus,
                                startTime: startTime.toISOString()
                            });

                            // Clear active call only if it hasn't been cleared already
                            if (activeCallRef.current) {
                                setActiveCall(null);
                                setSelectedProvider("");
                            }

                            // Show success message
                            const minutes = Math.floor(duration / 60);
                            const seconds = duration % 60;
                            toast.success(`Call completed. Duration: ${minutes > 0 ? `${minutes}m ` : ""}${seconds}s`);
                            
                            // Don't call onSuccess - it causes page refresh
                            // Call completion was already handled above
                        } catch (error) {
                            toast.error("Call completed but failed to update lead");
                        }
                    }
                }
            } catch (error) {
                // Don't stop polling on error, might be temporary
            }
        };

        // Start polling every 5 seconds
        pollingIntervalRef.current = setInterval(checkCallStatus, 5000);

        // Also check immediately
        checkCallStatus();

        // Cleanup on unmount or when activeCall changes
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };
    }, [activeCall, lead?._id, user._id, onSuccess]);

    // Get available VOIP providers
    const getVoipProviders = () => {
        const providers = [];
        if (user.voiso && user.permissions.voip?.voiso) {
            providers.push({
                id: "voiso",
                name: "Voiso",
                color: "bg-blue-600 hover:bg-blue-700",
                icon: PhoneCall
            });
        }
        if (user.coperato && user.permissions.voip?.coperato) {
            providers.push({
                id: "coperato",
                name: "Coperato",
                color: "bg-purple-600 hover:bg-purple-700",
                icon: PhoneCall
            });
        }
        if (user.commpeak && user.permissions.voip?.commpeak) {
            providers.push({
                id: "commpeak",
                name: "CommPeak",
                color: "bg-green-600 hover:bg-green-700",
                icon: Phone
            });
        }
        if (user.squaretalk && user.permissions.voip?.squaretalk) {
            providers.push({
                id: "squaretalk",
                name: "SquareTalk",
                color: "bg-orange-600 hover:bg-orange-700",
                icon: PhoneCall
            });
        }
        if (user.octella && user.permissions.voip?.octella) {
            providers.push({
                id: "octella",
                name: "Octella",
                color: "bg-teal-600 hover:bg-teal-700",
                icon: PhoneCall
            });
        }
        if (user.ccc && user.permissions.voip?.ccc) {
            providers.push({
                id: "ccc",
                name: "MMD Call Center Connect",
                color: "bg-indigo-600 hover:bg-indigo-700",
                icon: PhoneCall
            });
        }
        return providers;
    };

    const handleCall = async (provider) => {
        if (!formData.phone) {
            toast.error("Phone number is required to make a call");
            return;
        }

        setLoading(true);
        setSelectedProvider(provider.id);

        // Only SquareTalk needs activeCall for full call tracking
        // CommPeak, Coperato, and Voiso: simple notification only

        try {
            const callParams = {
                phoneNumber: formData.phone,
                agentId: user._id,
                leadId: lead._id
            };

            // Fire-and-forget for CommPeak (instant feedback, no waiting)
            if (provider.id === "commpeak") {
                // Send API call in background without waiting
                initiateCommpeakCall(callParams).catch(err => {
                    console.error("CommPeak call error (background):", err);
                    // Don't show error to user - they already got success message
                });
                
                // Immediately update UI and create log
                setActiveCall({
                    id: user.commpeak || `commpeak_${Date.now()}`,
                    provider: provider.id,
                    startTime: new Date(),
                    status: "calling", // Set to calling immediately
                    extension: user.commpeak
                });
                
                toast.success("📞 CommPeak call initiated! Your phone should ring shortly.");
                
                // Create call log immediately
                createLog({
                    logType: "call",
                    call: {
                        lead: lead._id,
                        staff: user._id,
                        provider: provider.id,
                        callId: user.commpeak || `commpeak_${Date.now()}`,
                        startTime: new Date()
                    }
                });
                
                setLoading(false);
                return; // Exit early for CommPeak
            }

            // For other providers, wait for response as before
            let callResult;
            
            // Initiate call based on provider
            switch (provider.id) {
                case "voiso":
                    callResult = await initiateVoisoCall(callParams);
                    break;
                case "coperato":
                    callResult = await initiateCoperatoCall(callParams);
                    break;
                case "squaretalk":
                    callResult = await initiateSquareTalkCall(callParams);
                    break;
                case "octella":
                    callResult = await initiateOctellaCall(callParams);
                    break;
                case "ccc":
                    callResult = await initiateCccCall(callParams);
                    break;
                default:
                    toast.error("Provider not supported");
                    setLoading(false);
                    return;
            }
            if (callResult.success) {
                // Extract callId from response (may be in callId, data.call_id, or data.id)
                const callId = callResult.callId || callResult.data?.call_id || callResult.data?.id || callResult.data?.callId;
                
                // For SquareTalk, also store the extension for ending the call
                const extension = callResult.extension || callResult.data?.meta?.extension || user.squaretalk;
                
                if (!callId && provider.id === "squaretalk") {
                    toast.warning("Call initiated but call ID not found. Status polling may not work.");
                }
                
                // Only SquareTalk needs full call tracking with End Call button and status polling
                // CommPeak, Coperato, and Voiso: Simple notification only
                if (provider.id === "squaretalk") {
                    setActiveCall({
                        id: callId || extension || user.squaretalk || `squaretalk_${Date.now()}`,
                        provider: provider.id,
                        startTime: new Date(),
                        status: "initiated",
                        extension: extension || user.squaretalk // Store extension for ending the call
                    });
                }

                toast.success("Call initiated successfully");
                // Add call log with provider information
                createLog({
                    logType: "call",
                    call: {
                        lead: lead._id,
                        staff: user._id,
                        provider: provider.id, // Include provider so calls can be counted by provider
                        callId: callId || extension || user[provider.id] || null,
                        startTime: new Date()
                    }
                });
            } else {
                // Surface the provider's real reason (e.g. missing config) instead of a generic message.
                const reason =
                    callResult?.error ||
                    callResult?.details?.error ||
                    callResult?.data?.error;
                toast.error(
                    reason
                        ? `Call failed: ${typeof reason === "string" ? reason : JSON.stringify(reason)}`
                        : "Failed to initiate call. Check your VOIP provider settings or try again later."
                );
            }
        } catch (error) {
            // Backend 4xx/5xx responses carry the specific reason in error.response.data.error.
            const reason =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                error?.message;
            toast.error(
                reason
                    ? `Call failed: ${typeof reason === "string" ? reason : JSON.stringify(reason)}`
                    : "Failed to initiate call. Check your VOIP provider settings or try again later."
            );
        } finally {
            setLoading(false);
        }
    };

    // Handle refresh call state
    const handleRefreshCall = () => {
        // Stop any polling that might be running
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
        
        // Clear all call-related state and refs
        activeCallRef.current = null;
        setActiveCall(null);
        setSelectedProvider("");
        setLoading(false); // Clear loading state to enable buttons immediately
        
        toast.success("Call state refreshed - you can make calls again");
    };

    // Handle end call
    const handleEndCall = async () => {
        if (!activeCall) {
            toast.error("No active call to end");
            return;
        }

        // CommPeak doesn't support ending calls via API
        if (activeCall.provider === "commpeak") {
            return;
        }

        setEndingCall(true);
        try {
            // For SquareTalk, use extension; for others, use callId
            const callIdentifier = activeCall.extension || activeCall.id;
            
            const result = await endCall(callIdentifier, activeCall.provider, activeCall.extension);
            
            if (result?.success) {
                toast.success("Call Ended", {
                    description: "The call has been terminated successfully"
                });
                
                // Calculate duration
                const endTime = new Date();
                const startTime = activeCall.startTime ? new Date(activeCall.startTime) : new Date();
                const duration = Math.floor((endTime - startTime) / 1000);
                
                // Handle call completion
                try {
                    await handleCallCompletion({
                        provider: activeCall.provider,
                        callId: activeCall.id,
                        leadId: lead._id,
                        agentId: user._id,
                        duration: duration,
                        status: "ended",
                        startTime: startTime.toISOString()
                    });
                } catch (error) {
                    // Error handling call completion - silent fail
                }
                
                // Clear active call
                setActiveCall(null);
                setSelectedProvider("");
                
                // Show duration
                const minutes = Math.floor(duration / 60);
                const seconds = duration % 60;
                toast.info(`Call ended. Duration: ${minutes > 0 ? `${minutes}m ` : ""}${seconds}s`);
                
                // Don't call onSuccess - it causes page refresh
                // Call was already logged via handleCallCompletion above
            } else {
                toast.error("Failed to End Call", {
                    description: result?.error || "Could not terminate the call"
                });
            }
        } catch (error) {
            toast.error("Failed to End Call", {
                description: error.message || "An error occurred while ending the call"
            });
        } finally {
            setEndingCall(false);
        }
    };

    const applyTemplatePlaceholders = (body) => {
        const balanceDisplay = formData.balance
            ? `${formData.balanceCurrency || 'USD'} ${formData.balance}`
            : '';
        return body
            .replace(/\{\{firstName\}\}/g, formData.firstName || '')
            .replace(/\{\{lastName\}\}/g, formData.lastName || '')
            .replace(/\{\{email\}\}/g, formData.email || '')
            .replace(/\{\{phone\}\}/g, formData.phone || '')
            .replace(/\{\{country\}\}/g, localLead?.country || '')
            .replace(/\{\{balance\}\}/g, balanceDisplay)
            .replace(/\{\{agentName\}\}/g, `${user.firstName} ${user.lastName}`);
    };

    const handleInsertTemplate = (template) => {
        setNewComment(applyTemplatePlaceholders(template.body));
        setShowTemplateDropdown(false);
    };

    const handleScreenshotUpload = async (event) => {
        try {
            const files = Array.from(event.target.files || []);
            if (!files.length) return;

            // Validate files
            const validFiles = files.filter(file => {
                const isImage = file.type.startsWith('image/');
                const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB limit
                if (!isImage) {
                    toast.error(`${file.name} is not an image file`);
                    return false;
                }
                if (!isValidSize) {
                    toast.error(`${file.name} exceeds 10MB limit`);
                    return false;
                }
                return true;
            });

            if (!validFiles.length) {
                event.target.value = null;
                return;
            }

            setUploadingScreenshots(true);
            
            const response = await uploadScreenshots(lead._id, validFiles);
            
            if (response?.status === 200 || response?.data?.success) {
                toast.success(response.data?.message || "Screenshots uploaded successfully");
                // Refresh lead data to get updated screenshots
                const res = await getLeadDataById(lead._id);
                if (res?.data?.screenshots) {
                    setScreenshots(res.data.screenshots);
                    // Update localLead with new screenshots
                    setLocalLead(prev => ({
                        ...prev,
                        screenshots: res.data.screenshots
                    }));
                }
            } else {
                toast.error(response?.data?.message || response?.message || "Failed to upload screenshots");
            }
        } catch (error) {
            console.error("Error uploading screenshots:", error);
            const errorMessage = error?.response?.data?.message || error?.message || "Failed to upload screenshots. Please try again.";
            toast.error(errorMessage);
        } finally {
            setUploadingScreenshots(false);
            // Reset file input
            if (event.target) {
                event.target.value = null;
            }
        }
    };

    const handleDeleteScreenshot = async (screenshotId) => {
        if (!window.confirm("Are you sure you want to delete this screenshot?")) {
            return;
        }

        try {
            const response = await deleteScreenshot(lead._id, screenshotId);
            if (response?.status === 200) {
                toast.success("Screenshot deleted successfully");
                setScreenshots(prev => prev.filter(s => s._id !== screenshotId));
                // Update localLead
                setLocalLead(prev => ({
                    ...prev,
                    screenshots: prev.screenshots.filter(s => s._id !== screenshotId)
                }));
            } else {
                toast.error(response?.message || "Failed to delete screenshot");
            }
        } catch (error) {
            console.error("Error deleting screenshot:", error);
            toast.error(error.response?.data?.message || error.message || "Failed to delete screenshot");
        }
    };

    const handleAddComment = () => {
        if (newComment.trim()) {
            const comment = {
                leadId: lead._id,
                text: newComment.trim(),
                author: user._id,
                createdAt: moment().tz("America/New_York").toDate()
            };

            createLeadComment(comment).then((response) => {
                if (response?.status === 200) {
                    // Use the comment from response if available, otherwise use local comment
                    const savedComment = response.data?.lead?.comments?.[response.data.lead.comments.length - 1] || comment;
                    setComments((prev) => [savedComment, ...prev]);
                    setNewComment("");
                    toast.success("Comment added successfully");
                } else if (response.data?.RESULT === "ERROR") {
                    toast.error(response.data.message);
                } else {
                    toast.error(response.message || "Failed to add comment");
                }
            }).catch((error) => {
                console.error("Error adding comment:", error);
                toast.error(error.response?.data?.message || error.message || "Failed to add comment. Please try again.");
            });
        };
    };

    const handleDeleteComment = async (commentId) => {
        if (!window.confirm("Are you sure you want to delete this comment?")) {
            return;
        }

        try {
            const response = await deleteLeadComment({
                leadId: lead._id,
                commentId: commentId
            });

            if (response?.status === 200) {
                // Remove comment from local state
                setComments((prev) => prev.filter((comment) => {
                    const id = comment._id || comment.id;
                    return id !== commentId;
                }));
                toast.success("Comment deleted successfully");
                
                // Refresh lead data to get updated comments
                await refreshLeadData();
            } else {
                toast.error(response.message || "Failed to delete comment");
            }
        } catch (error) {
            console.error("Error deleting comment:", error);
            toast.error(error.response?.data?.message || error.message || "Failed to delete comment. Please try again.");
        }
    };

    const handleDeleteLastComment = async () => {
        if (!window.confirm("Are you sure you want to delete this comment?")) {
            return;
        }

        if (!lead?._id) {
            toast.error("Cannot delete comment: Lead ID is missing");
            return;
        }

        try {
            const response = await updateLead({ 
                _id: lead._id, 
                lastComment: "" 
            });

            if (response?.status === 200) {
                toast.success("Comment deleted successfully");
                
                // Refresh lead data to get updated lastComment
                await refreshLeadData();
            } else {
                toast.error(response.message || "Failed to delete comment");
            }
        } catch (error) {
            console.error("Error deleting last comment:", error);
            toast.error(error.response?.data?.message || error.message || "Failed to delete comment. Please try again.");
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter" && e.shiftKey) {
            e.preventDefault();
            handleAddComment();
        }
    };

    // Navigation handlers with debounce and error handling
    const handlePrevious = async () => {
        // Debounce: prevent rapid clicks within 500ms
        const now = Date.now();
        if (now - lastNavigationClickRef.current < 500) {
            return;
        }
        lastNavigationClickRef.current = now;

        if (!canGoPrevious || !onNavigate || navigationLoading || loading) return;
        
        setNavigationLoading(true);
        
        try {
            const newIndex = currentLeadIndex - 1;
            const indexInPage = newIndex % pageSize;
            
            // Check if at the beginning of current page
            if (indexInPage < 0 || newIndex < 0) {
                // Reached beginning - refresh leads and restart from first
                toast.info("Finished calling current page. Refreshing leads...");
                
                // Trigger refresh
                if (onSuccess) {
                    onSuccess();
                }
                
                // Wait for refresh, then navigate to first lead
                setTimeout(() => {
                    // Go to page 1
                    if (onPageChange) {
                        onPageChange(1);
                    }
                    // Select first lead
                    if (filteredLeads.length > 0) {
                        onNavigate(filteredLeads[0], 0);
                    }
                }, 500);
                
                setNavigationLoading(false);
                return;
            }
            
            // Same page navigation
            if (indexInPage >= 0 && indexInPage < filteredLeads.length) {
                const targetLead = filteredLeads[indexInPage];
                if (targetLead) {
                    // Check Redux for latest version
                    const updatedLeadInRedux = allLeadsFromRedux.find(l => 
                        l._id?.toString() === targetLead._id?.toString()
                    );
                    onNavigate(updatedLeadInRedux || targetLead, newIndex);
                }
            }
        } catch (error) {
            console.error("Error navigating to previous lead:", error);
            toast.error("Failed to navigate. Please try again.");
        } finally {
            setNavigationLoading(false);
        }
    };

    const handleNext = async () => {
        // Debounce: prevent rapid clicks within 500ms
        const now = Date.now();
        if (now - lastNavigationClickRef.current < 500) {
            return;
        }
        lastNavigationClickRef.current = now;

        if (!canGoNext || !onNavigate || navigationLoading || loading) return;
        
        setNavigationLoading(true);
        
        try {
            const newIndex = currentLeadIndex + 1;
            const indexInPage = newIndex % pageSize;
            
            // Check if at the end of current page
            if (indexInPage >= filteredLeads.length || indexInPage === 0) {
                // Reached end of page - refresh leads and restart from first
                toast.info("Finished calling current page. Refreshing leads...");
                
                // Trigger refresh
                if (onSuccess) {
                    onSuccess();
                }
                
                // Wait for refresh, then navigate to first lead
                setTimeout(() => {
                    // Go to page 1
                    if (onPageChange) {
                        onPageChange(1);
                    }
                    // Select first lead
                    if (filteredLeads.length > 0) {
                        onNavigate(filteredLeads[0], 0);
                    }
                }, 500);
                
                setNavigationLoading(false);
                return;
            }
            
            // Same page navigation
            if (indexInPage >= 0 && indexInPage < filteredLeads.length) {
                const targetLead = filteredLeads[indexInPage];
                if (targetLead) {
                    // Check Redux for latest version
                    const updatedLeadInRedux = allLeadsFromRedux.find(l => 
                        l._id?.toString() === targetLead._id?.toString()
                    );
                    onNavigate(updatedLeadInRedux || targetLead, newIndex);
                }
            }
        } catch (error) {
            console.error("Error navigating to next lead:", error);
            toast.error("Failed to navigate. Please try again.");
        } finally {
            setNavigationLoading(false);
        }
    };

    const handleSave = async () => {
        setLoading(true);
        isSavingRef.current = true;
        try {
            // Only send changed fields to avoid overwriting desk/agent with undefined
            const updateData = {
                _id: lead._id,
                firstName: formData.firstName,
                lastName: formData.lastName,
                phone: formData.phone,
                email: formData.email,
                status: formData.status,
                source: formData.source,
                balance: formData.balance,
                balanceCurrency: formData.balanceCurrency,
                comments: comments
            };

            const response = await updateLead(updateData);
            if (response.status === 200) {
                toast.success("Lead updated successfully");
                // Update lead in Redux dynamically without refresh
                // API returns: { status: 200, data: { RESULT: "SUCCESS", lead: lead } }
                const apiLead = response.data?.lead || response.data?.data?.lead;
                
                // IMPORTANT: Use API response if available (has populated desk/agent)
                // Otherwise merge carefully to preserve populated fields
                const updatedLead = apiLead || {
                    ...lead,                    // Original lead with ALL populated fields
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    phone: formData.phone,
                    email: formData.email,
                    status: formData.status,
                    source: formData.source,
                    balance: formData.balance,
                    balanceCurrency: formData.balanceCurrency,
                    comments: comments,
                    _id: lead._id
                };
                
                // Update localLead immediately for instant UI feedback
                setLocalLead(updatedLead);
                
                // Call onLeadUpdate to update parent (Leads page Redux store)
                // Delay the call slightly to ensure save completes and modal stays stable
                // This updates Redux without causing modal to close
                if (onLeadUpdate) {
                    // Use requestAnimationFrame to defer the update until after the current render cycle
                    requestAnimationFrame(() => {
                        // Reset saving flag first, then update parent
                        isSavingRef.current = false;
                        onLeadUpdate(updatedLead);
                    });
                } else {
                    // If no onLeadUpdate, reset saving flag immediately
                    setTimeout(() => {
                        isSavingRef.current = false;
                    }, 100);
                }
            } else {
                toast.error("Failed to update lead");
            }
        } catch (error) {
            toast.error("Failed to update lead");
            // Reset saving flag on error
            setTimeout(() => {
                isSavingRef.current = false;
            }, 100);
        } finally {
            setLoading(false);
            // Note: Saving flag is reset in success path via requestAnimationFrame
            // This ensures parent updates happen after save completes without closing modal
        }
    };

    // Keyboard navigation - arrow keys to navigate between leads
    // MUST be before early return to follow Rules of Hooks
    useEffect(() => {
        if (!open || !hasNavigation || !lead) {
            return;
        }

        const handleKeyDown = (e) => {
            // Don't trigger if user is typing in an input, textarea, or select
            const isInputFocused = e.target.tagName === 'INPUT' || 
                                  e.target.tagName === 'TEXTAREA' || 
                                  e.target.tagName === 'SELECT' ||
                                  e.target.isContentEditable;
            
            if (isInputFocused || navigationLoading || loading) {
                return;
            }

            // Left arrow - previous lead
            if (e.key === 'ArrowLeft' && canGoPrevious) {
                e.preventDefault();
                handlePrevious();
            }
            
            // Right arrow - next lead
            if (e.key === 'ArrowRight' && canGoNext) {
                e.preventDefault();
                handleNext();
            }
        };

        // Add event listener
        window.addEventListener('keydown', handleKeyDown);

        // Cleanup
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [open, hasNavigation, canGoPrevious, canGoNext, loading, navigationLoading, handlePrevious, handleNext, lead]);

    // Early return AFTER all hooks
    if (!lead) return null;

    const voipProviders = getVoipProviders();

    return (
        <Dialog open={open} onOpenChange={handleDialogOpenChange}>
            <DialogContent className="max-w-[95vw] max-h-[95vh] overflow-y-auto">
                <DialogHeader className="border-b pb-3 sticky top-0 bg-background z-10 -mx-6 px-6 -mt-6 pt-6">
                    <div className="flex items-center justify-between">
                        <DialogTitle className="flex items-center text-xl font-bold text-gray-900">
                            <PhoneCall className="h-6 w-6 mr-3 text-blue-600" />
                            {getFullName(lead)}
                        </DialogTitle>
                        
                        {/* Navigation Controls */}
                        {hasNavigation && (
                            <div className="flex items-center space-x-2 bg-white/80 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-blue-200 shadow-sm">
                                <span className="text-sm text-blue-700 font-semibold mr-2">
                                    {leadPosition}
                                </span>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={handlePrevious}
                                    disabled={!canGoPrevious || loading || navigationLoading}
                                    title="Previous Lead"
                                    className="h-8 w-8 p-0 border-blue-300 hover:bg-blue-50"
                                >
                                    {navigationLoading ? (
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                    ) : (
                                        <ChevronLeft className="h-4 w-4 text-blue-600" />
                                    )}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={handleNext}
                                    disabled={!canGoNext || loading || navigationLoading}
                                    title="Next Lead"
                                    className="h-8 w-8 p-0 border-blue-300 hover:bg-blue-50"
                                >
                                    {navigationLoading ? (
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                    ) : (
                                        <ChevronRight className="h-4 w-4 text-blue-600" />
                                    )}
                                </Button>
                            </div>
                        )}
                    </div>
                </DialogHeader>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-3">
                    {/* Left Column - Lead Info & VOIP */}
                    <div className="space-y-4">
                        {/* Lead Information */}
                        <Card className="border-gray-200 shadow-sm">
                            <CardHeader className="bg-gray-50 border-b py-3">
                                <CardTitle className="flex items-center text-base font-semibold text-gray-900">
                                    <User className="h-5 w-5 mr-2 text-blue-600" />
                                    Lead Information
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3 pt-4">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label htmlFor="firstName" className="text-gray-700 text-sm font-medium">First Name</Label>
                                        <Input
                                            id="firstName"
                                            value={formData.firstName}
                                            onChange={(e) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    firstName: e.target.value
                                                }))
                                            }
                                            disabled={!user.permissions.editLeadFields?.firstName}
                                            className="h-9"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="lastName" className="text-gray-700 text-sm font-medium">Last Name</Label>
                                        <Input
                                            id="lastName"
                                            value={formData.lastName}
                                            onChange={(e) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    lastName: e.target.value
                                                }))
                                            }
                                            disabled={!user.permissions.editLeadFields?.lastName}
                                            className="h-9"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    {user.permissions.leads.viewPhone && (
                                        <div>
                                            <Label htmlFor="phone" className="text-gray-700 text-sm font-medium">Phone Number</Label>
                                            <Input
                                                id="phone"
                                                value={formData.phone}
                                                onChange={(e) =>
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        phone: e.target.value
                                                    }))
                                                }
                                                disabled={
                                                    !user.permissions.leads.viewPhone ||
                                                    !user.permissions.editLeadFields?.phone
                                                }
                                                className="font-mono h-9"
                                            />
                                        </div>
                                    )}

                                    {user.permissions.leads.viewEmail && (
                                        <div>
                                            <Label htmlFor="email" className="text-gray-700 text-sm font-medium">Email</Label>
                                            <div className="flex space-x-2">
                                                <Input
                                                    id="email"
                                                    type="email"
                                                    value={formData.email}
                                                    onChange={(e) =>
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            email: e.target.value
                                                        }))
                                                    }
                                                    disabled={!user.permissions.editLeadFields?.email}
                                                    className="flex-1 h-9"
                                                />
                                                {formData.email && (
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="icon"
                                                        onClick={async () => {
                                                            try {
                                                                await navigator.clipboard.writeText(formData.email);
                                                                setEmailCopied(true);
                                                                toast.success("Email copied to clipboard!");
                                                                setTimeout(() => setEmailCopied(false), 2000);
                                                            } catch (error) {
                                                                console.error("Failed to copy email:", error);
                                                                toast.error("Failed to copy email");
                                                            }
                                                        }}
                                                        className="shrink-0"
                                                        title="Copy email"
                                                    >
                                                        {emailCopied ? (
                                                            <Check className="h-4 w-4 text-green-600" />
                                                        ) : (
                                                            <Copy className="h-4 w-4" />
                                                        )}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    <div>
                                        <Label htmlFor="status" className="text-gray-700 text-sm font-medium">Status</Label>
                                        <Select
                                            options={leadStatuses}
                                            value={formData.status}
                                            onChange={(value) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    status: value
                                                }))
                                            }
                                            disabled={!user.permissions.editLeadFields?.status}
                                            searchable={true}
                                            searchPlaceholder="Search statuses..."
                                        />
                                    </div>

                                    <div>
                                        <Label htmlFor="source" className="text-gray-700 text-sm font-medium">Source</Label>
                                        <Select
                                            options={sourceOptions}
                                            value={formData.source}
                                            onChange={(value) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    source: value || ""
                                                }))
                                            }
                                            disabled={!user.permissions.editLeadFields?.source}
                                            searchable={true}
                                            searchPlaceholder="Search sources..."
                                            placeholder="Select source"
                                        />
                                    </div>

                                    <div>
                                        <Label htmlFor="balance" className="text-gray-700 text-sm font-medium">Balance (Amount in Bank)</Label>
                                        <div className="flex gap-2">
                                            <Select
                                                options={[
                                                    { label: "USD ($)", value: "USD" },
                                                    { label: "EUR (€)", value: "EUR" },
                                                    { label: "CAD ($)", value: "CAD" },
                                                    { label: "GBP (£)", value: "GBP" },
                                                    { label: "AUD ($)", value: "AUD" }
                                                ]}
                                                value={formData.balanceCurrency || "USD"}
                                                onChange={(value) =>
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        balanceCurrency: value
                                                    }))
                                                }
                                                className="w-32"
                                            />
                                            <Input
                                                id="balance"
                                                value={formData.balance}
                                                onChange={(e) =>
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        balance: e.target.value
                                                    }))
                                                }
                                                placeholder="e.g., 5000"
                                                className="h-9 text-sm flex-1"
                                                type="number"
                                                step="0.01"
                                            />
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Country & Local Time Display */}
                                {lead.country && (
                                    <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-200 shadow-sm">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-2">
                                                <MapPin className="h-4 w-4 text-blue-600" />
                                                <span className="text-xs font-medium text-gray-700">Country:</span>
                                                <span className="text-sm text-blue-900 font-bold">{lead.country}</span>
                                            </div>
                                            {localTime && (
                                                <div className="flex items-center space-x-2 bg-white px-2.5 py-1 rounded-md border border-blue-300 shadow-sm">
                                                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                                    <span className="text-xs font-mono font-bold text-blue-900">{localTime}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                
                                <div className="flex justify-end space-x-2 mt-3">
                                    <Button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleSave();
                                        }}
                                        disabled={loading}
                                        size="sm"
                                        className="bg-green-600 hover:bg-green-700:bg-green-600 text-white shadow-sm"
                                    >
                                        <Save className="h-3.5 w-3.5 mr-1.5" />
                                        {loading ? "Saving..." : "Save Lead"}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>

                        {/* VOIP Providers */}
                        <Card className="border-blue-200 shadow-sm">
                            <CardHeader className="bg-gray-50 border-b py-3">
                                <CardTitle className="flex items-center text-base font-semibold text-blue-900">
                                    <Phone className="h-5 w-5 mr-2 text-blue-600" />
                                    Call Providers
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-4">
                                {activeCall && (
                                    <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg shadow-sm">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center space-x-2">
                                                <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse shadow-sm"></div>
                                                <span className="font-semibold text-green-800 text-sm">
                                                    {activeCall.status === "initiating" ? "Initiating Call..." : "Call Active"}
                                                </span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                {activeCall.provider !== "commpeak" && activeCall.status !== "initiating" && (
                                                    <Button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            handleEndCall();
                                                        }}
                                                        variant="destructive"
                                                        size="sm"
                                                        disabled={endingCall}
                                                        className="text-white"
                                                    >
                                                        {endingCall ? (
                                                            <>
                                                                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                                                Ending...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <PhoneOff className="h-4 w-4 mr-1" />
                                                                End Call
                                                            </>
                                                        )}
                                                    </Button>
                                                )}
                                                <Button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleRefreshCall();
                                                    }}
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-green-700 hover:text-green-800 hover:bg-green-100"
                                                    title="Clear call state"
                                                >
                                                    <RefreshCw className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                        <p className="text-sm text-green-600">
                                            Provider: {activeCall.provider.toUpperCase()}
                                            {activeCall.startTime && ` | Started: ${format(activeCall.startTime, "HH:mm:ss")}`}
                                        </p>
                                        {activeCall.extension && (
                                            <p className="text-xs text-green-500 mt-1">
                                                Extension: {activeCall.extension}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {voipProviders.length > 0 ? (
                                    <div className="space-y-2">
                                        {voipProviders.map((provider, index) => {
                                            const IconComponent = provider.icon;
                                            const isActiveProvider = selectedProvider === provider.id;
                                            return (
                                                <Button
                                                    key={provider.id + index}
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleCall(provider);
                                                    }}
                                                    className={`w-full ${provider.color} text-white relative shadow-sm hover:shadow-md transition-all h-10 font-medium`}
                                                    disabled={
                                                        !formData.phone ||
                                                        loading ||
                                                        activeCall
                                                    }
                                                >
                                                    <IconComponent className="h-4 w-4 mr-2" />
                                                    {loading && isActiveProvider ? (
                                                        <>
                                                            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                                            Connecting...
                                                        </>
                                                    ) : (
                                                        `Call via ${provider.name}`
                                                    )}
                                                </Button>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="text-center py-4 text-gray-500">
                                        <PhoneCall className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                        <p>No VOIP providers configured</p>
                                        <p className="text-sm">Contact admin to set up call providers</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right Column - Comments */}
                    <div className="space-y-4">
                        <Card className="flex flex-col border-gray-200 shadow-sm">
                            <CardHeader className="bg-gray-50 border-b py-3">
                                <CardTitle className="flex items-center text-base font-semibold text-gray-900">
                                    <MessageCircle className="h-5 w-5 mr-2 text-blue-600" />
                                    Comments ({comments.length})
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="flex-1 flex flex-col p-0">
                                {/* Add Comment Section */}
                                <div className="p-3 border-b border-gray-200 bg-gray-50">
                                    <div className="mb-2 flex gap-2">
                                        {/* Template picker dropdown */}
                                        <div className="relative">
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                onClick={() => setShowTemplateDropdown((v) => !v)}
                                                className="text-xs text-blue-700 bg-blue-50 border-blue-300 hover:bg-blue-100"
                                            >
                                                📋 {templates.length > 0 ? "Insert Template" : "No Templates"}
                                            </Button>
                                            {showTemplateDropdown && templates.length > 0 && (
                                                <>
                                                    <div className="fixed inset-0 z-40" onClick={() => setShowTemplateDropdown(false)} />
                                                    <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[220px] max-h-60 overflow-y-auto">
                                                    {templates.map((t) => (
                                                        <button
                                                            key={t._id}
                                                            type="button"
                                                            onClick={() => handleInsertTemplate(t)}
                                                            className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 border-b border-gray-100 last:border-0 text-gray-800 font-medium"
                                                        >
                                                            {t.name}
                                                        </button>
                                                    ))}
                                                </div>
                                                </>
                                            )}
                                        </div>
                                        <label className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-300 hover:bg-green-100 rounded-md transition-colors cursor-pointer">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                multiple
                                                onChange={handleScreenshotUpload}
                                                className="hidden"
                                                disabled={uploadingScreenshots}
                                            />
                                            <ImagePlus className="h-3.5 w-3.5 mr-1" />
                                            {uploadingScreenshots ? "Uploading..." : "Upload Screenshots"}
                                        </label>
                                    </div>
                                    <div className="flex gap-2">
                                        <Textarea
                                            placeholder="Add a comment..."
                                            value={newComment}
                                            onChange={(e) => setNewComment(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            rows={6}
                                            className="flex-1 resize-y text-sm min-h-[100px]"
                                        />
                                        <Button
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleAddComment();
                                            }}
                                            disabled={!newComment.trim()}
                                            size="sm"
                                            className="self-end bg-blue-600 hover:bg-blue-700:bg-blue-600 h-8 w-8 p-0"
                                        >
                                            <Send className="h-4 w-4" />
                                        </Button>
                                    </div>
                                    <p className="text-xs text-gray-600 mt-1.5">
                                        Press Enter for new line, Shift+Enter to send
                                    </p>
                                </div>

                                {/* Comments List */}
                                <div className="flex-1 overflow-y-auto p-3">
                                    {comments.length === 0 && !lead?.lastComment ? (
                                        <div className="text-center text-gray-400 py-8">
                                            <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                            <p className="font-medium text-gray-600">No comments yet</p>
                                            <p className="text-sm text-gray-500">Add the first comment above</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3 max-h-[300px] overflow-y-auto">
                                            {/* Display lastComment only if it's NOT already in the comments array (to avoid duplicates) */}
                                            {lead?.lastComment && !comments.some(comment => comment.text === lead.lastComment) && (
                                                <div className="p-3 rounded-lg bg-yellow-50 border-l-4 border-yellow-500 shadow-sm">
                                                    <div className="flex justify-between items-start mb-2 gap-2">
                                                        <div className="flex flex-col">
                                                            <span className="text-sm font-medium text-yellow-800">
                                                                Last Comment
                                                            </span>
                                                            {lead.lastCommStaff && (
                                                                <span className="text-xs text-yellow-700 mt-0.5">
                                                                    by {getFullName(lead.lastCommStaff)}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2 flex-shrink-0">
                                                            <span className="text-xs text-gray-500">
                                                                {lead.lastCommDate 
                                                                    ? format(new Date(lead.lastCommDate), "MMM dd, HH:mm")
                                                                    : (lead.updatedAt ? format(new Date(lead.updatedAt), "MMM dd, HH:mm") : "")
                                                                }
                                                            </span>
                                                            {user.position.role === "admin" && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={handleDeleteLastComment}
                                                                    className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                    title="Delete comment"
                                                                >
                                                                    <Trash2 className="h-4 w-4" />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                                        {lead.lastComment}
                                                    </p>
                                                </div>
                                            )}
                                            {comments.map((comment) => {
                                                const commentId = comment._id || comment.id;
                                                const ai = comment.aiCall;
                                                const isAi = !!ai;
                                                const hasCallDetail = isAi && (ai.transcript || ai.recordingUrl);
                                                return (
                                                    <div
                                                        key={comment.id}
                                                        className={`p-3 rounded-lg border shadow-sm ${
                                                            isAi
                                                                ? "bg-indigo-50 border-l-4 border-indigo-500"
                                                                : comment.isSystem
                                                                    ? "bg-blue-50 border-l-4 border-blue-500"
                                                                    : "bg-white border-gray-200"
                                                        }`}
                                                    >
                                                        <div className="flex justify-between items-start mb-2">
                                                            <span
                                                                className={`text-sm font-semibold flex items-center gap-1.5 ${
                                                                    isAi ? "text-indigo-800" : comment.isSystem ? "text-blue-800" : "text-gray-900"
                                                                }`}
                                                            >
                                                                {isAi && <Bot className="h-3.5 w-3.5" />}
                                                                {comment.affiliateName 
                                                                    ? `${isAi ? "" : "Comment by "}${comment.affiliateName}`
                                                                    : users.length
                                                                        ? getFullName(
                                                                              users.find((u) => u._id === comment.author)
                                                                          ) || "Unknown User"
                                                                        : ""}
                                                            </span>
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-xs text-gray-500 font-medium">
                                                                    {format(new Date(comment.createdAt), "MMM dd, HH:mm")}
                                                                </span>
                                                                {user.position.role === "admin" && (
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => handleDeleteComment(commentId)}
                                                                        className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                        title="Delete comment"
                                                                    >
                                                                        <Trash2 className="h-4 w-4" />
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <p className="text-sm text-gray-900 whitespace-pre-wrap">
                                                            {comment.text}
                                                        </p>
                                                        {isAi && (
                                                            <div className="mt-2 flex flex-wrap items-center gap-2">
                                                                {typeof ai.durationSeconds === "number" && (
                                                                    <span className="text-xs text-indigo-700 bg-indigo-100 rounded px-1.5 py-0.5">
                                                                        {Math.floor(ai.durationSeconds / 60)}m {ai.durationSeconds % 60}s
                                                                    </span>
                                                                )}
                                                                {ai.sentiment && (
                                                                    <span className="text-xs text-indigo-700 bg-indigo-100 rounded px-1.5 py-0.5">
                                                                        {ai.sentiment}
                                                                    </span>
                                                                )}
                                                                {hasCallDetail && (
                                                                    <Button
                                                                        variant="outline"
                                                                        size="sm"
                                                                        onClick={() => setTranscriptCall({ ...ai, createdAt: comment.createdAt })}
                                                                        className="h-6 px-2 text-xs text-indigo-700 border-indigo-300 hover:bg-indigo-100"
                                                                    >
                                                                        <FileText className="h-3.5 w-3.5 mr-1" />
                                                                        View transcript / recording
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Screenshots Card - Collapsible */}
                        {screenshots.length > 0 && (
                            <Card className="border-gray-200 shadow-sm">
                                <CardHeader 
                                    className="pb-2 py-2 cursor-pointer hover:bg-gray-50:bg-gray-800 transition-colors"
                                    onClick={() => setShowScreenshots(!showScreenshots)}
                                >
                                    <CardTitle className="flex items-center justify-between text-xs font-semibold text-gray-900 uppercase tracking-wide">
                                        <div className="flex items-center">
                                            <ImagePlus className="h-3.5 w-3.5 mr-1.5 text-gray-600" />
                                            Screenshots
                                            <span className="ml-2 text-xs font-normal text-gray-500">
                                                ({screenshots.length})
                                            </span>
                                        </div>
                                        <svg
                                            className={`h-4 w-4 text-gray-600 transition-transform ${showScreenshots ? 'rotate-180' : ''}`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </CardTitle>
                                </CardHeader>
                                {showScreenshots && (
                                    <CardContent className="pt-2 pb-3">
                                    <div className="grid grid-cols-3 gap-1.5">
                                        {screenshots.map((screenshot) => (
                                            <div key={screenshot._id} className="relative group">
                                                <a
                                                    href={`${serviceConstants.API_HOST_NAME}${screenshot.path}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="block aspect-square rounded border border-gray-200 overflow-hidden hover:border-blue-400 transition-colors"
                                                >
                                                    <img
                                                        src={`${serviceConstants.API_HOST_NAME}${screenshot.path}`}
                                                        alt={screenshot.originalName}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => {
                                                            e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><text x="50%" y="50%" text-anchor="middle" fill="gray">No Image</text></svg>';
                                                        }}
                                                    />
                                                </a>
                                                <div className="absolute top-0.5 right-0.5 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <a
                                                        href={`${serviceConstants.API_HOST_NAME}${screenshot.path}`}
                                                        download={screenshot.originalName}
                                                        className="p-0.5 bg-white rounded shadow hover:bg-gray-100"
                                                        title="Download"
                                                    >
                                                        <Download className="h-3 w-3 text-gray-700" />
                                                    </a>
                                                    {user.position.role === "admin" && (
                                                        <button
                                                            onClick={() => handleDeleteScreenshot(screenshot._id)}
                                                            className="p-0.5 bg-white rounded shadow hover:bg-red-50"
                                                            title="Delete"
                                                        >
                                                            <X className="h-3 w-3 text-red-600" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                                )}
                            </Card>
                        )}

                    </div>
                </div>

                <div className="flex justify-end items-center mt-4 pt-4 border-t border-gray-200 bg-gray-50 -mx-6 px-6 -mb-6 pb-6">
                    <div className="flex space-x-2">
                        <Button type="button" onClick={onClose} variant="outline" size="sm">
                            Close
                        </Button>
                    </div>
                </div>
            </DialogContent>

            {/* AI call transcript / recording modal */}
            <Dialog open={!!transcriptCall} onOpenChange={(v) => !v && setTranscriptCall(null)}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Bot className="h-4 w-4 text-indigo-600" /> AI Call Details
                        </DialogTitle>
                    </DialogHeader>
                    {transcriptCall && (
                        <div className="space-y-4">
                            <div className="flex flex-wrap gap-2 text-xs">
                                {transcriptCall.createdAt && (
                                    <span className="bg-gray-100 rounded px-2 py-1 text-gray-700">
                                        {format(new Date(transcriptCall.createdAt), "MMM dd, yyyy HH:mm")}
                                    </span>
                                )}
                                {typeof transcriptCall.durationSeconds === "number" && (
                                    <span className="bg-gray-100 rounded px-2 py-1 text-gray-700">
                                        {Math.floor(transcriptCall.durationSeconds / 60)}m {transcriptCall.durationSeconds % 60}s
                                    </span>
                                )}
                                {transcriptCall.sentiment && (
                                    <span className="bg-indigo-100 rounded px-2 py-1 text-indigo-700">
                                        {transcriptCall.sentiment}
                                    </span>
                                )}
                            </div>
                            {transcriptCall.recordingUrl && (
                                <div>
                                    <p className="text-xs font-semibold text-gray-700 mb-1">Recording</p>
                                    <audio controls className="w-full" src={transcriptCall.recordingUrl} />
                                </div>
                            )}
                            {transcriptCall.callSummary && (
                                <div>
                                    <p className="text-xs font-semibold text-gray-700 mb-1">Summary</p>
                                    <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{transcriptCall.callSummary}</p>
                                </div>
                            )}
                            {transcriptCall.transcript ? (
                                <div>
                                    <p className="text-xs font-semibold text-gray-700 mb-1">Transcript</p>
                                    <pre className="text-xs text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap font-sans max-h-96 overflow-y-auto">{transcriptCall.transcript}</pre>
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500">No transcript available for this call.</p>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

        </Dialog>
    );
}
